import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MarketHeaderData } from '../hooks/useMarketHeaderData';
import { Msg } from '../i18n-review/client/Msg';

export interface MarketHeaderSeoBlocksProps {
  data: MarketHeaderData;
}

/**
 * JSON-LD (Dataset + FAQPage) and the sr-only Q&A / percentile / work-model / language tables —
 * moved verbatim out of MarketHeader.tsx so MarketRail and MarketStatCards both emit the exact
 * same SEO payload regardless of which visible layout is showing. Do not touch this content;
 * it is a non-negotiable constraint (see the SRP density implementation plan).
 */
export function MarketHeaderSeoBlocks({ data }: MarketHeaderSeoBlocksProps): React.ReactElement {
  const intl = useIntl();
  const {
    structuredData,
    locationHeaderText,
    description,
    translatedLocation,
    resultsHeadingText,
    qaContent,
    salaryPercentileSeoData,
    workModelData,
    languageData,
  } = data;

  return (
    <>
      {/* The one semantic <h1> for this section — MarketRail and MarketStatCards render matching
          visible titles as aria-hidden <p> so exactly one <h1> exists regardless of viewport. */}
      {/* resultsHeadingText is dynamically composed per-locale (generateResultsHeading), not a
          static react-intl catalogue string — same convention as the <title>/description text. */}
      <h1 className="sr-only">{resultsHeadingText}</h1>

      {/* Structured Data (JSON-LD) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData.dataset) }}
      />
      {structuredData.faqPage && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData.faqPage) }}
        />
      )}

      {/* Q&A structure for AI Overviews - hidden but crawlable */}
      {(description || locationHeaderText.overviewText) && (
        <div className="sr-only">
          <p>
            <strong>
              <Msg
                id="marketHeader.qa.remoteQuestion"
                defaultMessage="Is {location} remote-friendly?"
                values={{ location: translatedLocation }}
                description="Question in Q&A section about remote work availability in a location (hidden, for SEO)"
              />
            </strong>{' '}
            <Msg
              id="marketHeader.qa.remoteAnswer"
              defaultMessage="Yes, {answer}"
              values={{ answer: qaContent.remote }}
              description="Answer in Q&A section about remote work availability (hidden, for SEO)"
            />
          </p>
          <p>
            <strong>
              <Msg
                id="marketHeader.qa.salaryQuestion"
                defaultMessage="What is the average salary in {location}?"
                values={{ location: translatedLocation }}
                description="Question in Q&A section about average salary in a location (hidden, for SEO)"
              />
            </strong>{' '}
            {qaContent.salary}
          </p>
          <p>
            <strong>
              <Msg
                id="marketHeader.qa.languageQuestion"
                defaultMessage="What languages are required in {location}?"
                values={{ location: translatedLocation }}
                description="Question in Q&A section about language requirements in a location (hidden, for SEO)"
              />
            </strong>{' '}
            {qaContent.language}
          </p>
        </div>
      )}

      {/* Visually Hidden Table for Google Answer Box (sr-only) */}
      <div className="sr-only">
        <table>
          <caption>
            <Msg
              id="marketHeader.salaryPercentileSeo.caption"
              defaultMessage="Advertised salary distribution in {location}, Finland (full-time roles)"
              values={{ location: translatedLocation.split(',')[0]?.trim() || translatedLocation }}
              description="Hidden table caption for location salary percentile data"
            />
          </caption>
          <thead>
            <tr>
              <th scope="col">
                <Msg
                  id="marketHeader.salaryPercentileSeo.percentile"
                  defaultMessage="Percentile"
                  description="Hidden location salary percentile table header"
                />
              </th>
              <th scope="col">
                <Msg
                  id="marketHeader.salaryPercentileSeo.salary"
                  defaultMessage="Monthly salary (EUR)"
                  description="Hidden location salary percentile table header"
                />
              </th>
            </tr>
          </thead>
          {salaryPercentileSeoData ? (
            <tbody>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.salaryPercentileSeo.p25"
                    defaultMessage="25th percentile"
                    description="Hidden location salary percentile row label"
                  />
                </td>
                <td>€{Math.round(salaryPercentileSeoData.percentile25).toLocaleString()}/month</td>
              </tr>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.salaryPercentileSeo.p50"
                    defaultMessage="Median (50th percentile)"
                    description="Hidden location salary percentile row label"
                  />
                </td>
                <td>€{Math.round(salaryPercentileSeoData.percentile50).toLocaleString()}/month</td>
              </tr>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.salaryPercentileSeo.p75"
                    defaultMessage="75th percentile"
                    description="Hidden location salary percentile row label"
                  />
                </td>
                <td>€{Math.round(salaryPercentileSeoData.percentile75).toLocaleString()}/month</td>
              </tr>
            </tbody>
          ) : null}
        </table>
        {salaryPercentileSeoData?.sampleSize ? (
          <p>
            <Msg
              id="marketHeader.salaryPercentileSeo.sampleSize"
              defaultMessage="Based on {count} job postings with explicit salary data in {location}, Finland."
              values={{
                count: intl.formatNumber(salaryPercentileSeoData.sampleSize),
                location: translatedLocation.split(',')[0]?.trim() || translatedLocation,
              }}
              description="Hidden explanatory text under location salary percentile table"
            />
          </p>
        ) : null}
        {workModelData.total > 0 ? (
          <table>
            <caption>
              <Msg
                id="marketHeader.workModelSeo.caption"
                defaultMessage="Work model distribution in {location}"
                values={{ location: translatedLocation }}
                description="Hidden table caption for work model distribution"
              />
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  <Msg
                    id="marketHeader.workModelSeo.model"
                    defaultMessage="Work model"
                    description="Hidden work model table header"
                  />
                </th>
                <th scope="col">
                  <Msg
                    id="marketHeader.workModelSeo.share"
                    defaultMessage="Share of listings"
                    description="Hidden work model table header"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.workModelSeo.onsite"
                    defaultMessage="Onsite"
                    description="Hidden work model table body cell"
                  />
                </td>
                <td>{Math.round((workModelData.onsite / workModelData.total) * 100)}%</td>
              </tr>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.workModelSeo.hybrid"
                    defaultMessage="Hybrid"
                    description="Hidden work model table body cell"
                  />
                </td>
                <td>{Math.round((workModelData.hybrid / workModelData.total) * 100)}%</td>
              </tr>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.workModelSeo.remote"
                    defaultMessage="Remote"
                    description="Hidden work model table body cell"
                  />
                </td>
                <td>{Math.round((workModelData.remote / workModelData.total) * 100)}%</td>
              </tr>
            </tbody>
          </table>
        ) : null}
        {languageData.total > 0 ? (
          <table>
            <caption>
              <Msg
                id="marketHeader.languageSeo.caption"
                defaultMessage="Language demand in {location}"
                values={{ location: translatedLocation }}
                description="Hidden table caption for language demand distribution"
              />
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  <Msg
                    id="marketHeader.languageSeo.language"
                    defaultMessage="Language category"
                    description="Hidden language demand table header"
                  />
                </th>
                <th scope="col">
                  <Msg
                    id="marketHeader.languageSeo.share"
                    defaultMessage="Share of listings"
                    description="Hidden language demand table header"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.languageSeo.english"
                    defaultMessage="English"
                    description="Hidden language demand table body cell"
                  />
                </td>
                <td>{Math.round((languageData.english / languageData.total) * 100)}%</td>
              </tr>
              <tr>
                <td>
                  <Msg
                    id="marketHeader.languageSeo.localLanguages"
                    defaultMessage="Local languages"
                    description="Hidden language demand table body cell"
                  />
                </td>
                <td>{Math.round((languageData.local / languageData.total) * 100)}%</td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </div>
    </>
  );
}
